import React from 'react';
import { colors } from '../theme/tokens';

/**
 * AiErrorBoundary — wraps the AI suggestion bar and AI settings panel.
 * If anything in the AI feature throws, this catches it and shows a small
 * inline warning instead of crashing the rest of the inbox.
 *
 * Use the `inline` prop for the suggestion bar (shows a compact error strip).
 * Without `inline` it shows a fuller panel error (for the settings page).
 */
class AiErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[AiErrorBoundary] AI feature error:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.inline) {
      return (
        <div style={styles.inlineBar}>
          <span style={styles.inlineIcon}>⚠️</span>
          <span style={styles.inlineText}>AI assistant unavailable — the rest of the inbox is unaffected.</span>
          <button
            style={styles.inlineRetry}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div style={styles.panelError}>
        <div style={styles.panelIcon}>🤖</div>
        <div style={styles.panelTitle}>AI Assistant Unavailable</div>
        <div style={styles.panelMsg}>
          Something went wrong in the AI module. The rest of the inbox continues to work normally.
        </div>
        {this.state.error?.message && (
          <div style={styles.panelDetail}>{this.state.error.message}</div>
        )}
        <button
          style={styles.panelBtn}
          onClick={() => this.setState({ hasError: false, error: null })}
        >
          Try Again
        </button>
      </div>
    );
  }
}

const styles = {
  inlineBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    background: 'rgba(240,168,48,0.14)',
    borderTop: '1px solid rgba(240,168,48,0.4)',
    fontSize: '12px',
    color: '#f0c68a'
  },
  inlineIcon: { fontSize: '14px', flexShrink: 0 },
  inlineText: { flex: 1 },
  inlineRetry: {
    background: 'transparent',
    border: '1px solid rgba(240,168,48,0.5)',
    color: '#f0c68a',
    borderRadius: '4px',
    padding: '2px 10px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    flexShrink: 0
  },
  panelError: {
    padding: '40px 24px',
    textAlign: 'center',
    maxWidth: '400px',
    margin: '0 auto'
  },
  panelIcon: { fontSize: '40px', marginBottom: '12px' },
  panelTitle: {
    fontWeight: '700',
    fontSize: '16px',
    color: colors.text,
    marginBottom: '8px'
  },
  panelMsg: {
    fontSize: '13px',
    color: colors.textMuted,
    marginBottom: '8px',
    lineHeight: '1.6'
  },
  panelDetail: {
    fontSize: '11px',
    color: colors.textFaint,
    fontStyle: 'italic',
    marginBottom: '16px'
  },
  panelBtn: {
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  }
};

export default AiErrorBoundary;
